/**
 * Course settings · "Plăți și facturi": the payment provider, its keys and
 * the online payments switch, SmartBill invoicing, and what the gateway itself
 * says happened beside what we recorded.
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Settings, CreditCard, ToggleLeft, ToggleRight, Loader2, Send, FileText } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { Card, Row, HealthChecklist } from "./shared";

export default function PaymentSettings({ data, save, canMutate, busy, onReload }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="settings-tab-plati">
      <PaymentProviderCard data={data} save={save} canMutate={canMutate} busy={busy} />
      {/* The configuration of the provider that is active now, so this card
          always matches the selector beside it. The online payments switch
          lives inside it and gates both providers. */}
      {data.payment_provider === "euplatesc" ? (
        <EuPlatescConfigCard data={data} save={save} canMutate={canMutate} busy={busy} />
      ) : (
        <NetopiaConfigCard data={data} save={save} canMutate={canMutate} busy={busy} />
      )}
      {/* Next to the payment cards because that is the order things happen
          in: the provider takes the money, SmartBill issues the invoice. */}
      <SmartBillCard data={data} canMutate={canMutate} onReload={onReload} />
      <EuPlatescLedgerCard />
    </div>
  );
}

function PaymentProviderCard({ data, save, canMutate, busy }) {
  return (
    <Card title="Provider plată activ" icon={<CreditCard size={16} />} testid="settings-card-payment-provider">
      <p className="text-sm text-[#6B7280] mb-3">
        Providerul care va procesa plățile noi. Plățile deja inițiate continuă cu providerul cu care au fost create
        (purchase-urile rețin slug-ul în câmpul <code className="bg-[#EDEEF0] px-1 rounded text-xs">provider</code>).
      </p>
      <div className="space-y-2" data-testid="settings-payment-provider-list">
        {(data.available_payment_providers || []).map((p) => {
          const active = data.payment_provider === p.id;
          const disabled = !canMutate || busy !== null || !p.configured;
          return (
            <label
              key={p.id}
              className={`flex items-start gap-3 rounded-xl border p-3 ${active ? "border-[#965EA7] bg-[#965EA7]/[0.04]" : "border-[#E5E7EB] hover:border-[#965EA7]/40"} ${disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
              data-testid={`settings-payment-provider-${p.id}`}
            >
              <input
                type="radio"
                name="payment_provider"
                value={p.id}
                checked={active}
                onChange={() => save({ payment_provider: p.id }, "provider plată")}
                disabled={disabled}
                className="mt-1 accent-[#965EA7]"
              />
              <div className="text-sm text-[#373A45] flex-1">
                <div className="font-semibold flex items-center gap-2">
                  {p.display_name}
                  {active && (
                    <span className="inline-flex rounded-full bg-[#965EA7]/15 text-[#965EA7] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Activ</span>
                  )}
                </div>
                <div className="text-xs text-[#6B7280] mt-0.5">
                  {p.configured ? (
                    <span className="text-[#1F7A3B]">Configurat</span>
                  ) : (
                    <span className="text-[#7A1F26]">Neconfigurat — completează cheile în Render → Environment.</span>
                  )}
                </div>
              </div>
            </label>
          );
        })}
      </div>
      <div className="mt-3 text-xs text-[#6B7280]">
        Schimbarea providerului afectează doar plățile <strong>noi</strong>. Plățile existente continuă pe providerul lor.
      </div>
    </Card>
  );
}

/**
 * M902 — Netopia configuration card.
 *
 * Original right-hand card, untouched in behaviour. The only change vs.
 * the pre-M902 inline block is that the bonus 5-point HealthChecklist now
 * sits at the top for fast diagnosis.
 */
function NetopiaConfigCard({ data, save, canMutate, busy }) {
  const d = data.netopia_diagnostics || {};
  const items = [
    { label: "Configurat", ok: !!data.netopia_configured },
    { label: "API Key", ok: !!d.api_key_present },
    { label: "IPN Key", ok: !!data.netopia_ipn_verification_configured },
    { label: data.netopia_live ? "Mod LIVE" : "Mod Sandbox", ok: !!data.netopia_configured },
  ];
  return (
    <Card title="Netopia Payments" icon={<CreditCard size={16} />} testid="settings-card-netopia">
      <HealthChecklist items={items} testid="settings-netopia-health" />
      <div className="text-sm space-y-2">
        <Row label="Configurat în .env (Render)" value={data.netopia_configured ? "Da" : "Nu"} ok={data.netopia_configured} />
        <Row label="Mod" value={data.netopia_live ? "LIVE" : "Sandbox"} ok={!data.netopia_live} />
        <Row label="Semnătură POS (ultimele 4)" value={data.netopia_signature_preview ? `····${data.netopia_signature_preview}` : "—"} mono />
        <Row label="API Key separat" value={d.api_key_present ? "Da" : "Nu (folosește signature)"} ok={d.api_key_present} />
        <Row label="Authorization header din" value={d.auth_header_source || "—"} mono />
        <Row label="Endpoint" value={d.base_url || "—"} mono />
        <Row label="Verificare IPN (cheie publică RSA)" value={data.netopia_ipn_verification_configured ? "Configurată" : "Lipsă"} ok={data.netopia_ipn_verification_configured} />
        <Row label="Format cheie publică" value={d.public_key_type || "—"} mono />
        <Row label="Sursă cheie IPN" value={d.ipn_public_key_source || "—"} mono />
      </div>
      {d.missing_required_fields?.length > 0 && (
        <div
          className="mt-3 p-3 rounded-lg bg-[#FDECEC] border border-[#E63946]/30 text-xs text-[#7A1F26]"
          data-testid="settings-netopia-missing-fields"
        >
          <strong>Lipsesc variabile obligatorii pentru mod {data.netopia_live ? "LIVE" : "Sandbox"}:</strong>
          <ul className="mt-1 ml-4 list-disc font-mono">
            {d.missing_required_fields.map((f) => (<li key={f}>{f}</li>))}
          </ul>
          <div className="mt-2 font-sans">
            Plățile sunt refuzate până când acestea sunt setate în Render → Environment.
          </div>
        </div>
      )}
      {!d.api_key_present && data.netopia_configured && !data.netopia_live && (
        <div className="mt-3 p-3 rounded-lg bg-[#FFF7E6] border border-[#F5C76E] text-xs text-[#7A5A1B]">
          <strong>Atenție:</strong> NETOPIA_API_KEY nu este setat. Folosim NETOPIA_SIGNATURE
          ca token în Authorization. Dacă Netopia returnează 401, generează un API Key
          din panoul Netopia → <em>Profile → Security → API Key</em> și adaugă-l ca
          secret în Render: <code className="font-mono">NETOPIA_API_KEY=...</code>.
        </div>
      )}
      <PaymentsKillSwitch data={data} save={save} canMutate={canMutate} busy={busy} provider="netopia" />
    </Card>
  );
}

/**
 * M902 — EuPlătesc configuration card.
 *
 * Mirrors the Netopia card layout so the right-hand panel feels "the same
 * card with different content". Secret values NEVER appear here — only
 * the masked merchant id + boolean "configured" flags coming from the
 * backend (see EuPlatescProvider.diagnostics()).
 */
function EuPlatescConfigCard({ data, save, canMutate, busy }) {
  const d = data.euplatesc_diagnostics || {};
  const configured = !!data.euplatesc_configured;
  const items = [
    { label: "Configurat", ok: configured },
    { label: "Merchant ID", ok: !!d.merchant_id_masked },
    { label: "Secret Key", ok: !!d.secret_configured },
    { label: "Callback", ok: !!d.callback_configured },
    { label: d.live ? "Mod LIVE" : "Mod Sandbox", ok: configured },
  ];
  return (
    <Card title="EuPlătesc" icon={<CreditCard size={16} />} testid="settings-card-euplatesc">
      <HealthChecklist items={items} testid="settings-euplatesc-health" />
      <div className="text-sm space-y-2">
        <Row label="Configurat în .env (Render)" value={configured ? "Da" : "Nu"} ok={configured} />
        <Row label="Mod" value={d.live ? "LIVE" : "Sandbox"} ok={!d.live} />
        <Row label="Merchant ID" value={d.merchant_id_masked || "—"} mono />
        <Row label="Secret Key" value={d.secret_configured ? "Configurat" : "Lipsă"} ok={d.secret_configured} />
        <Row label="Endpoint" value={d.endpoint || "—"} mono />
        <Row
          label="Callback (IPN)"
          value={d.callback_configured ? "Configurat în panoul merchant" : "—"}
          ok={d.callback_configured}
        />
      </div>
      {d.missing_required_fields?.length > 0 && (
        <div
          className="mt-3 p-3 rounded-lg bg-[#FDECEC] border border-[#E63946]/30 text-xs text-[#7A1F26]"
          data-testid="settings-euplatesc-missing-fields"
        >
          <strong>Lipsesc variabile obligatorii pentru mod {d.live ? "LIVE" : "Sandbox"}:</strong>
          <ul className="mt-1 ml-4 list-disc font-mono">
            {d.missing_required_fields.map((f) => (<li key={f}>{f}</li>))}
          </ul>
          <div className="mt-2 font-sans">
            Plățile sunt refuzate până când acestea sunt setate în Render → Environment.
          </div>
        </div>
      )}
      <div className="mt-3 p-3 rounded-lg bg-[#EEF2FF] border border-[#965EA7]/20 text-xs text-[#373A45]">
        <strong>Notă:</strong> EuPlătesc nu validează URL-ul de callback prin env. Setează
        URL-ul <code className="font-mono bg-white px-1 rounded">/api/payment/euplatesc/ipn</code>
        direct în panoul tău EuPlătesc → <em>Settings → Merchant → IPN URL</em>.
      </div>
      <PaymentsKillSwitch data={data} save={save} canMutate={canMutate} busy={busy} provider="euplatesc" />
    </Card>
  );
}

/**
 * M902 — Kill switch for online payments, shared by both provider cards.
 *
 * The Mongo setting `netopia_enabled` is the legacy name for the master
 * "online payments on/off" flag — kept stable to avoid a Mongo migration.
 * Gating: the toggle is disabled when the ACTIVE provider isn't configured.
 */
function PaymentsKillSwitch({ data, save, canMutate, busy, provider }) {
  const isNetopia = provider === "netopia";
  const isConfigured = isNetopia ? !!data.netopia_configured : !!data.euplatesc_configured;
  const providerLabel = isNetopia ? "Netopia" : "EuPlătesc";
  return (
    <div className="mt-4 pt-4 border-t border-[#E5E7EB] flex items-center justify-between gap-3">
      <div className="text-sm text-[#373A45]">
        <div className="font-semibold">Activează {providerLabel} pentru generarea plăților</div>
        <div className="text-xs text-[#6B7280] mt-0.5">
          Permite backend-ului să creeze plăți pentru checkout-ul online ȘI pentru linkurile
          de plată trimise din dashboard. Independent de modul checkout-ului public.
        </div>
        <div className="text-[11px] text-[#6B7280] mt-1">
          <strong>Atenție:</strong> nu confunda cu „LIVE/Sandbox&rdquo; — mediul efectiv este setat
          separat prin variabila de env <code className="font-mono bg-[#EDEEF0] px-1 rounded">{isNetopia ? "NETOPIA_LIVE" : "EUPLATESC_LIVE"}</code>{" "}
          și apare la rândul „Mod&rdquo; din tabelul de mai sus.
        </div>
      </div>
      <button
        disabled={!canMutate || !isConfigured || busy !== null}
        onClick={() => save({ netopia_enabled: !data.netopia_enabled }, `${providerLabel} provider`)}
        className="text-[#965EA7] disabled:opacity-40 shrink-0"
        data-testid={`settings-${provider}-toggle`}
        title={
          !isConfigured
            ? `Configurează mai întâi cheile ${providerLabel} în .env`
            : data.netopia_enabled
              ? `Click pentru a dezactiva plățile online`
              : `Click pentru a activa plățile online`
        }
      >
        {data.netopia_enabled ? <ToggleRight size={36} /> : <ToggleLeft size={36} />}
      </button>
    </div>
  );
}

/**
 * SmartBill invoicing status.
 *
 * Every other integration fails loudly. SmartBill does not: invoicing is
 * fire-and-forget by design, so when it is unconfigured a paid order simply
 * never gets an invoice — no error, no email, nothing in the logs. This card
 * is the only surface where that state is visible, which is why it leads with
 * the health checklist rather than with the last invoice.
 *
 * "De facturat" is counted with the backfill cron's own query, so the number
 * here is exactly what the next run will pick up.
 */
function SmartBillCard({ data, canMutate, onReload }) {
  const [running, setRunning] = useState(false);
  const d = data.smartbill_diagnostics || {};
  const stats = data.smartbill_stats || {};
  const configured = !!data.smartbill_configured;
  const missing = d.missing_required_fields || [];
  // Credentials all present but the kill switch is explicitly off — a
  // different problem from missing keys, and a different fix.
  const switchedOff = !configured && missing.length === 0 && d.enabled === false;
  const last = stats.last_invoice || null;

  const runBackfill = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/cron/smartbill-backfill");
      const { issued = 0, collected = 0, failed = 0, skipped } = r.data || {};
      if (skipped === "not_configured") {
        toast.error("SmartBill nu este configurat — nu s-a emis nimic.");
      } else {
        toast.success(`Facturi emise: ${issued} · încasări: ${collected} · eșecuri: ${failed}`);
      }
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la rularea facturării.");
    } finally {
      setRunning(false);
    }
  };

  const items = [
    { label: "Username", ok: !!d.username_masked },
    { label: "Token", ok: !!d.token_configured },
    { label: "CIF", ok: !!d.cif },
    { label: "Serie", ok: !!d.series },
    { label: "Comutator", ok: d.enabled !== false },
  ];

  return (
    <Card title="Facturare SmartBill" icon={<FileText size={16} />} testid="settings-card-smartbill">
      <HealthChecklist items={items} testid="settings-smartbill-health" />
      <div className="text-sm space-y-2">
        <Row label="Configurat în .env (Render)" value={configured ? "Da" : "Nu"} ok={configured} />
        <Row label="Cont" value={d.username_masked || "—"} mono />
        <Row label="Serie facturi" value={d.series || "—"} mono />
        <Row label="CIF" value={d.cif || "—"} mono />
        <Row
          label="Ultima factură"
          value={last?.smartbill_number
            ? `${last.smartbill_series || ""}${last.smartbill_number}`
            : "Nicio factură emisă"}
          ok={last?.smartbill_number ? true : undefined}
          mono={!!last?.smartbill_number}
        />
        <Row
          label="De facturat"
          value={stats.pending === 0 ? "0 — la zi" : `${stats.pending ?? "—"} plăți`}
          ok={stats.pending === 0 ? true : stats.pending > 0 ? false : undefined}
        />
      </div>

      {missing.length > 0 && (
        <div
          className="mt-3 p-3 rounded-lg bg-[#FDECEC] border border-[#E63946]/30 text-xs text-[#7A1F26]"
          data-testid="settings-smartbill-missing-fields"
        >
          <strong>Lipsesc variabile obligatorii:</strong>
          <ul className="mt-1 ml-4 list-disc font-mono">
            {missing.map((f) => (<li key={f}>{f}</li>))}
          </ul>
          <div className="mt-2 font-sans">
            Până la setarea lor în Render → Environment, plățile se încasează normal, dar
            <strong> nu se emite nicio factură</strong> și nimic nu semnalează asta în altă parte.
          </div>
        </div>
      )}

      {switchedOff && (
        <div
          className="mt-3 p-3 rounded-lg bg-[#FDF3E7] border border-[#E0B356]/40 text-xs text-[#8a6a1a]"
          data-testid="settings-smartbill-disabled"
        >
          Cheile sunt complete, dar <code className="font-mono bg-white px-1 rounded">SMARTBILL_ENABLED</code>{" "}
          este setat pe <code className="font-mono bg-white px-1 rounded">false</code>. Facturarea e oprită intenționat.
        </div>
      )}

      {stats.last_error && (
        <div className="mt-3 text-xs text-[#9b1f2c]" data-testid="settings-smartbill-last-error">
          <strong>Ultima eroare:</strong> {stats.last_error}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-[#E5E7EB] flex items-center justify-between gap-3">
        <div className="text-sm text-[#373A45]">
          <div className="font-semibold">Rulează facturarea acum</div>
          <div className="text-xs text-[#6B7280] mt-0.5">
            Emite facturile pentru plățile confirmate care nu au încă una. Rulează oricum
            automat la fiecare 30 de minute — butonul e pentru când nu vrei să aștepți.
          </div>
        </div>
        <button
          onClick={runBackfill}
          disabled={!canMutate || running || !configured}
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#965EA7] text-white text-sm font-semibold px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="settings-smartbill-backfill"
          title={configured ? "" : "Configurează mai întâi cheile SmartBill în Render"}
        >
          {running ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
          {running ? "Se rulează…" : "Rulează"}
        </button>
      </div>
    </Card>
  );
}

/**
 * EuPlătesc's own record, compared with ours.
 *
 * We only ever learn an outcome when the gateway sends a notification. One
 * that goes missing — or one we cannot verify, as happened for two days with
 * declined cards — leaves an order in a state that is simply wrong, and the
 * only way to find out was to export from their panel and compare by hand.
 * This card does that comparison.
 *
 * It changes nothing. `Diferențe` is precisely what a reconciliation sweep
 * would correct, shown first so the sweep can be judged before it is built.
 */
function EuPlatescLedgerCard() {
  const [probe, setProbe] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  // Which date the window filters on. Their default is the completion date,
  // which a declined transaction never gets — so the default view can hide
  // exactly the failures this card exists to find.
  const [by, setBy] = useState("");
  const [sweep, setSweep] = useState(null);
  const [sweeping, setSweeping] = useState(false);

  const runProbe = useCallback(async () => {
    try {
      const r = await api.get("/admin/euplatesc/ws-status");
      setProbe(r.data);
    } catch (err) {
      // OUR endpoint failed, not theirs — and the difference matters. The
      // first live check ran against a frontend that had deployed before the
      // backend, so this route 404'd and the card reported FastAPI's "Not
      // Found" as though EuPlătesc had said it. That sent a whole round of
      // debugging at the wrong system.
      setProbe({ localError: apiErrorMessage(err, "Nu am putut contacta serverul.") });
    }
  }, []);

  useEffect(() => { runProbe(); }, [runProbe]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/euplatesc/transactions", {
        params: by ? { days, by } : { days },
      });
      setLedger(r.data);
      if (!r.data?.ok) toast.error(r.data?.error || "Interogarea a eșuat.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  /** Run the correction pass now, instead of waiting for the hourly cron.
   *
   *  Corrects statuses only. Nothing is invoiced and no customer hears from
   *  us — an outcome discovered days late is a person's decision to announce. */
  const runSweep = async (dryRun) => {
    setSweeping(true);
    try {
      const r = await api.post("/admin/cron/euplatesc-reconcile", null, {
        params: { days, dry_run: dryRun, ...(by ? { by } : {}) },
      });
      setSweep({ ...r.data, was_dry_run: dryRun });
      if (!r.data?.ok) toast.error(r.data?.error || "Verificarea a eșuat.");
      else if (!r.data.corrected_count && !r.data.needs_review_count) {
        toast.success("Nimic de corectat — totul se potrivește.");
      } else if (dryRun) {
        toast.success(`${r.data.corrected_count} ar fi corectate.`);
      } else {
        toast.success(`${r.data.corrected_count} comenzi corectate.`);
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSweeping(false);
    }
  };

  const p = probe?.probe;
  const reachable = p?.ok === true;

  return (
    <section
      className="bg-white rounded-2xl border border-[#E5E7EB] p-5"
      data-testid="euplatesc-ledger-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-[#373A45]">Registrul EuPlătesc</h3>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            probe?.localError || p == null ? "bg-[#F3F4F6] text-[#6B7280]"
              : reachable ? "bg-[#EAF4EE] text-[#2F6B4E]"
              : "bg-[#FDECEC] text-[#9b1f2c]"
          }`}
          data-testid="euplatesc-ws-state"
        >
          {probe?.localError ? "Neverificat"
            : p == null ? "Se verifică…"
            : reachable ? "Conectat" : "Indisponibil"}
        </span>
      </div>

      <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">
        Ce spune EuPlătesc că s-a întâmplat, față de ce am înregistrat noi.
        Doar citire — nimic nu se modifică de aici.
      </p>

      {probe?.localError && (
        <div
          className="mt-3 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2.5 text-sm text-[#6B7280]"
          data-testid="euplatesc-ws-local-error"
        >
          Nu am putut întreba serverul nostru: <strong>{probe.localError}</strong>.
          Dacă tocmai s-a făcut un deploy, reîncarcă pagina peste un minut —
          nu spune nimic despre EuPlătesc.
        </div>
      )}

      {p && !reachable && (
        <div
          className="mt-3 rounded-xl border border-[#E63946]/30 bg-[#E63946]/5 px-3 py-2.5 text-sm text-[#9b1f2c]"
          data-testid="euplatesc-ws-error"
        >
          {p.error === "not_configured" ? (
            "Cheile EuPlătesc nu sunt configurate."
          ) : (
            <>
              <div>
                Serviciul de raportare a răspuns: <strong>{p.error}</strong>
                {p.http_status ? ` (HTTP ${p.http_status})` : ""}
              </div>

              {/* Per method, because the two answers mean different things:
                  one failing and the other working is an entitlement, both
                  failing is the endpoint or the merchant id. Without the
                  split, the first live probe said only "Not Found" and there
                  was nothing to act on. */}
              {probe?.probes && (
                <ul className="mt-2 space-y-1 text-xs" data-testid="euplatesc-ws-probes">
                  {Object.entries(probe.probes).map(([name, r]) => (
                    <li key={name}>
                      <span className="font-mono">{name}</span>
                      {" — "}
                      {r.ok === true ? "răspunde" : r.ok === null ? "netestat" : (r.error || "eșuat")}
                    </li>
                  ))}
                </ul>
              )}

              {p.body && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer">Răspunsul lor, brut</summary>
                  <pre className="mt-1 whitespace-pre-wrap break-all text-[11px] opacity-80">
                    {p.body}
                  </pre>
                </details>
              )}

              <p className="mt-2 text-xs leading-relaxed">
                Trimite-le acest răspuns și întreabă dacă serviciul web trebuie
                activat pentru MID-ul vostru, sau dacă cere o cheie separată.
              </p>
            </>
          )}
        </div>
      )}

      {reachable && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-full border border-[#E5E7EB] px-3 py-1.5 text-sm"
              data-testid="euplatesc-ledger-days"
            >
              {[2, 7, 14, 30].map((d) => (
                <option key={d} value={d}>ultimele {d} zile</option>
              ))}
            </select>
            <select
              value={by}
              onChange={(e) => setBy(e.target.value)}
              className="rounded-full border border-[#E5E7EB] px-3 py-1.5 text-sm"
              data-testid="euplatesc-ledger-by"
            >
              <option value="">după data lor implicită</option>
              <option value="action">după data tranzacției</option>
              <option value="complete">după data decontării</option>
            </select>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="rounded-full bg-[#373A45] hover:bg-[#1F2128] disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5"
              data-testid="euplatesc-ledger-load"
            >
              {loading ? "Se încarcă…" : "Compară"}
            </button>
          </div>

          {/* Comparing is read-only; correcting is not. The two are separate
              buttons, and the destructive-sounding one goes through a dry run
              first, because "see what it would do" is the question anybody
              sensible asks before letting a job rewrite payment records. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => runSweep(true)}
              disabled={sweeping}
              className="rounded-full border border-[#3F8EBA]/40 text-[#2B6285] hover:bg-[#EAF2F8] disabled:opacity-60 text-sm font-semibold px-4 py-1.5"
              data-testid="euplatesc-sweep-dry"
            >
              {sweeping ? "Se verifică…" : "Vezi ce ar corecta"}
            </button>
            <button
              type="button"
              onClick={() => runSweep(false)}
              disabled={sweeping}
              className="rounded-full bg-[#3F8EBA] hover:bg-[#2B6285] disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5"
              data-testid="euplatesc-sweep-run"
            >
              Corectează acum
            </button>
            <span className="text-xs text-[#6B7280]">
              Corectează doar statusul. Fără facturi, fără emailuri către cursanți.
            </span>
          </div>

          {sweep?.ok && (
            <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] p-3" data-testid="euplatesc-sweep-result">
              <div className="flex flex-wrap gap-4 text-sm">
                <span>
                  <strong>{sweep.corrected_count}</strong>{" "}
                  {sweep.was_dry_run ? "ar fi corectate" : "corectate"}
                </span>
                {sweep.needs_review_count > 0 && (
                  <span className="text-[#B45309] font-semibold">
                    <strong>{sweep.needs_review_count}</strong> de verificat manual
                  </span>
                )}
                <span className="text-[#6B7280]">{sweep.checked} verificate</span>
              </div>

              {sweep.corrected?.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs" data-testid="euplatesc-sweep-corrected">
                  {sweep.corrected.slice(0, 25).map((c) => (
                    <li key={c.purchase_id} className="text-[#373A45]">
                      <span className="font-mono">{c.invoice_id}</span>
                      {" — "}{c.email}{": "}
                      <span className="text-[#6B7280]">{c.our_status}</span>
                      {" → "}
                      <strong>{c.their_status}</strong>
                    </li>
                  ))}
                </ul>
              )}

              {sweep.needs_review?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-[#B45309]">
                    Nemodificate — la noi sunt plătite sau rambursate, la ei nu:
                  </p>
                  <ul className="mt-1 space-y-1 text-xs" data-testid="euplatesc-sweep-review">
                    {sweep.needs_review.slice(0, 25).map((n) => (
                      <li key={n.purchase_id}>
                        <span className="font-mono">{n.invoice_id}</span>
                        {" — "}{n.email}{": "}
                        <span className="text-[#6B7280]">{n.our_status}</span>
                        {" vs "}
                        <strong>{n.their_status}</strong>
                        {n.invoiced ? " · facturată" : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {ledger?.ok && (
            <div className="mt-4" data-testid="euplatesc-ledger-result">
              <div className="flex flex-wrap gap-4 text-sm">
                <span><strong>{ledger.total}</strong> tranzacții la ei</span>
                <span
                  className={ledger.mismatch_count ? "text-[#B45309] font-semibold" : "text-[#2F6B4E]"}
                  data-testid="euplatesc-ledger-mismatches"
                >
                  <strong>{ledger.mismatch_count}</strong> diferențe
                </span>
                {ledger.unknown_to_us > 0 && (
                  <span className="text-[#6B7280]">
                    {ledger.unknown_to_us} necunoscute nouă
                  </span>
                )}
              </div>

              {ledger.mismatch_count > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-[#6B7280] uppercase tracking-wide">
                      <tr>
                        <th className="text-left py-1.5">Comandă</th>
                        <th className="text-left py-1.5">La noi</th>
                        <th className="text-left py-1.5">La ei</th>
                        <th className="text-left py-1.5">Motiv</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.mismatches.slice(0, 25).map((m) => (
                        <tr key={m.invoice_id} className="border-t border-[#E5E7EB]">
                          <td className="py-1.5 font-mono">{m.invoice_id}</td>
                          <td className="py-1.5 text-[#6B7280]">{m.our_status || "—"}</td>
                          <td className="py-1.5 font-semibold text-[#B45309]">{m.their_status}</td>
                          <td className="py-1.5 text-[#6B7280]">{m.their_message || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-xs text-[#6B7280] leading-relaxed">
                    Astea sunt comenzile pe care o reconciliere le-ar corecta. Deocamdată
                    doar se afișează.
                  </p>
                </div>
              )}

              {ledger.mismatch_count === 0 && ledger.total > 0 && (
                <p className="mt-2 text-sm text-[#2F6B4E]">
                  Totul se potrivește — nicio comandă nu are alt status la ei.
                </p>
              )}

              {/* Zero is a real answer only if nobody paid. It is also what a
                  reply in a shape we are not reading looks like — which has
                  now happened twice — so an empty window shows what actually
                  arrived instead of a reassuring number. */}
              {ledger.total === 0 && (
                <div
                  className="mt-3 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2.5 text-sm text-[#6B7280]"
                  data-testid="euplatesc-ledger-empty"
                >
                  <p className="leading-relaxed">
                    Nicio tranzacție în interval. Dacă știi că au existat plăți,
                    încearcă „după data tranzacției" — filtrul lor implicit se
                    uită la data decontării, pe care o plată refuzată nu o are.
                  </p>
                  {ledger.reply && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer">Ce au răspuns, brut</summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all text-[11px] opacity-80">
                        {JSON.stringify(ledger.reply, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
